/**
 * confirmToast — undo-style destructive confirmation.
 *
 * Usage:
 *   confirmToast({
 *     message: "Cliente eliminato",
 *     description: "Puoi ripristinarlo entro 5 secondi",
 *     onConfirm: async () => { await api.delete(...); }
 *   })
 *
 * The action runs OPTIMISTICALLY after the countdown, unless the user clicks "Annulla".
 * If you need a HARD confirm (block before running), use hardConfirm().
 */
import { toast } from "sonner";

export function confirmToast({ message, description, onConfirm, onCancel, duration = 5000, actionLabel = "Annulla" }) {
  let cancelled = false;
  const id = toast(message, {
    description,
    duration,
    action: {
      label: actionLabel,
      onClick: () => {
        cancelled = true;
        if (onCancel) onCancel();
      },
    },
  });
  setTimeout(async () => {
    if (cancelled) return;
    try {
      await onConfirm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'operazione");
    }
  }, duration);
  return id;
}

/**
 * hardConfirm — replacement for window.confirm() using Sonner promise UI.
 * Returns a Promise<boolean>.
 */
export function hardConfirm({ title, description, confirmLabel = "Conferma", cancelLabel = "Annulla", danger = false }) {
  return new Promise((resolve) => {
    const id = toast(title, {
      description,
      duration: 30000,
      action: {
        label: confirmLabel,
        onClick: () => { toast.dismiss(id); resolve(true); },
      },
      cancel: {
        label: cancelLabel,
        onClick: () => { toast.dismiss(id); resolve(false); },
      },
      className: danger ? "!bg-red-50 !border-red-300" : undefined,
    });
  });
}
